/**
 * The password sign-in. Wears the shop this device last signed into — name, mark, look and
 * colour scheme — before anybody is identified, so a Diamond till opens dark and cyan rather
 * than on another shop's cream.
 *
 * A device that has never signed in gets the neutral state: the neutral look, the generic mark
 * and the app's own name — not the product's. The till is one address serving every shop, and
 * the product being named after its first client is not a reason to open on that client's name.
 * See `useRememberedShop`.
 *
 * Top-anchored on a phone rather than centred: the keyboard takes the lower half the moment the
 * first field is tapped, and a centred form is then shoved upward mid-focus. Anchoring it means
 * nothing moves. On a wide screen there is no keyboard to plan for, so it becomes a card on the
 * shop's backdrop like the rest of the till.
 */

import { readHint, useRememberedShop } from '@pos/shared';
import { neutralBrand } from '@pos/tokens';

import { PasswordLoginForm } from '@/features/password-login';
import { SavedSaleNotice } from '@/features/sale-checkout';
import { ShopMark } from '@/shared/ui';

export function SignInPage() {
  const hint = readHint();
  const remembered = useRememberedShop();

  return (
    <div className="flex h-full flex-col overflow-y-auto px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(3.5rem,env(safe-area-inset-top))] md:h-auto md:overflow-visible md:p-0">
      <div className="rise-in w-full md:rounded-sheet md:border md:border-line-subtle md:bg-surface-base md:p-8 md:shadow-column">
        <header className="mb-8">
          <ShopMark
            themeCode={remembered?.themeCode ?? null}
            neutral={remembered === null}
            size={56}
            className="mb-5"
          />
          <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-content-primary">
            {remembered?.name ?? neutralBrand.pos}
          </h1>
          <p className="mt-1.5 text-base text-content-muted">Sign in to start selling</p>
        </header>

        {/* Prefilled when a previous user's hint survives — a shared counter machine usually has
            the same two or three people on it, and re-typing a username forty times a week is
            friction with no security value. */}
        <div className="mb-5 empty:hidden">
          <SavedSaleNotice userId={hint?.userId} fullName={hint?.fullName} />
        </div>

        <PasswordLoginForm username={hint?.username ?? ''} />

        {/* A reload asks for the password only because no PIN is set; six digits is the cheap
            way back, and this is the moment the person feels why. */}
        {hint !== null && !hint.hasPin && (
          <p className="mt-5 text-center text-xs text-content-muted">
            Set a PIN in Profile, and after a reload the till asks only for your 6 digits.
          </p>
        )}
      </div>
    </div>
  );
}
